<template>
  <div
    aria-label="Formulaire de changement de mot de passe fournisseur"
    role="main"
  >
    <!-- Fil d'Ariane (Breadcrumb) -->
    <b-card
      class="mb-3"
      aria-label="Fil d'Ariane navigation"
    >
      <b-breadcrumb>
        <b-breadcrumb-item to="/">
          <feather-icon
            icon="HomeIcon"
            size="16"
            class="mr-25"
          />
          <span class="align-middle">Accueil</span>
        </b-breadcrumb-item>
        <b-breadcrumb-item to="/suppliers">
          Fournisseurs
        </b-breadcrumb-item>
        <b-breadcrumb-item :to="{ name: 'supplier-show', params: { id: $route.params.id }}">
          Détail fournisseur
        </b-breadcrumb-item>
        <b-breadcrumb-item active>
          Changer le mot de passe
        </b-breadcrumb-item>
      </b-breadcrumb>
    </b-card>

    <!-- État de chargement -->
    <b-overlay
      :show="isLoading"
      rounded="sm"
      spinner-variant="primary"
      aria-busy="true"
      aria-live="polite"
    >
      <!-- État d'erreur -->
      <b-alert
        v-if="error"
        variant="danger"
        show
        aria-live="assertive"
        role="alert"
      >
        {{ error }}
      </b-alert>

      <!-- Formulaire de changement de mot de passe -->
      <b-card
        :title="null"
        aria-labelledby="change-password-title"
      >
        <h1
          id="change-password-title"
          class="sr-only"
        >
          Formulaire de changement de mot de passe fournisseur
        </h1>
        <validation-observer
          ref="formValidator"
          v-slot="{ handleSubmit }"
        >
          <b-form
            aria-describedby="form-desc"
            role="form"
            @submit.prevent="handleSubmit(onSubmit)"
          >
            <div
              id="form-desc"
              class="sr-only"
            >
              Saisissez un nouveau mot de passe sécurisé pour le fournisseur sélectionné. Tous les champs sont obligatoires.
            </div>
            <b-card-text class="mb-3">
              <div
                class="alert alert-info"
                role="status"
              >
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  class="mr-1"
                  aria-hidden="true"
                />
                <span>Vous modifiez le mot de passe pour le fournisseur : <strong>{{ supplierName }}</strong></span>
              </div>
            </b-card-text>

            <!-- Champs de mot de passe -->
            <b-row>
              <!-- Nouveau mot de passe -->
              <b-col md="6">
                <b-form-group
                  label="Nouveau mot de passe *"
                  label-for="password"
                  description="Le mot de passe doit comporter au moins 8 caractères, inclure des majuscules, chiffres et caractères spéciaux."
                  aria-required="true"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Mot de passe"
                    rules="required|min:8"
                    vid="password"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        id="password"
                        v-model="passwordForm.password"
                        :type="showPassword ? 'text' : 'password'"
                        :state="errors.length > 0 ? false : passwordStrength > 25"
                        placeholder="Entrez un nouveau mot de passe sécurisé"
                        aria-required="true"
                        autocomplete="new-password"
                      />
                      <b-input-group-append
                        is-text
                        aria-label="Afficher ou masquer le mot de passe"
                        @click="showPassword = !showPassword"
                      >
                        <feather-icon
                          :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          class="cursor-pointer"
                          aria-hidden="true"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>

                    <!-- Indicateur de robustesse du mot de passe -->
                    <div class="mt-1">
                      <div class="d-flex justify-content-between mb-1">
                        <small>Force du mot de passe :</small>
                        <small class="text-right">{{ passwordStrengthText }}</small>
                      </div>
                      <b-progress
                        :value="passwordStrength"
                        :variant="passwordStrengthClass"
                        max="100"
                        height="8px"
                        aria-label="Indicateur de robustesse du mot de passe"
                      />
                    </div>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Confirmation du mot de passe -->
              <b-col md="6">
                <b-form-group
                  label="Confirmer le mot de passe *"
                  label-for="confirmPassword"
                  description="Saisissez à nouveau le mot de passe pour confirmation."
                  aria-required="true"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Confirmation du mot de passe"
                    rules="required|confirmed:password"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        id="confirmPassword"
                        v-model="passwordForm.confirm_password"
                        :type="showConfirmPassword ? 'text' : 'password'"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Confirmez le mot de passe"
                        aria-required="true"
                        autocomplete="new-password"
                      />
                      <b-input-group-append
                        is-text
                        aria-label="Afficher ou masquer la confirmation du mot de passe"
                        @click="showConfirmPassword = !showConfirmPassword"
                      >
                        <feather-icon
                          :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          class="cursor-pointer"
                          aria-hidden="true"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <div
              class="d-flex justify-content-between mt-4"
              role="group"
              aria-label="Actions du formulaire"
            >
              <b-button
                variant="outline-secondary"
                :to="{ name: 'supplier-show', params: { id: $route.params.id }}"
                aria-label="Annuler et revenir au détail fournisseur"
              >
                <feather-icon
                  icon="XIcon"
                  class="mr-1"
                  aria-hidden="true"
                />
                <span>Annuler</span>
              </b-button>

              <b-button
                variant="primary"
                type="submit"
                :disabled="isSubmitting || !canSubmit"
                aria-label="Enregistrer le nouveau mot de passe"
              >
                <feather-icon
                  icon="SaveIcon"
                  class="mr-1"
                  aria-hidden="true"
                />
                <span>Enregistrer</span>
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required, confirmed, min } from 'vee-validate/dist/rules'
import storeAuth from '@/store/store'
import {
  BCard,
  BBreadcrumb,
  BBreadcrumbItem,
  BButton,
  BAlert,
  BOverlay,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BCardText,
  BRow,
  BCol,
  BProgress,
} from 'bootstrap-vue'

// Register the validation rules
extend('required', required)
extend('confirmed', confirmed)
extend('min', min)

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BCard,
    BBreadcrumb,
    BBreadcrumbItem,
    BButton,
    BAlert,
    BOverlay,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BCardText,
    BRow,
    BCol,
    BProgress,
  },

  data() {
    return {
      passwordForm: {
        password: '',
        confirm_password: '',
      },
      supplier: {},
      isLoading: false,
      isSubmitting: false,
      error: null,
      showPassword: false,
      showConfirmPassword: false,
      user: {},
    }
  },

  computed: {
    supplierName() {
      return this.supplier.username || ''
    },

    passwordStrength() {
      let strength = 0
      const { password } = this.passwordForm

      if (!password) return 0

      // Length contribution
      if (password.length >= 8) strength += 25

      // Character type contribution
      if (/[A-Z]/.test(password)) strength += 25 // Uppercase
      if (/[0-9]/.test(password)) strength += 25 // Numbers
      if (/[^A-Za-z0-9]/.test(password)) strength += 25 // Special chars

      return strength
    },

    passwordStrengthClass() {
      if (this.passwordStrength <= 25) return 'danger'
      if (this.passwordStrength <= 50) return 'warning'
      if (this.passwordStrength <= 75) return 'info'
      return 'success'
    },

    passwordStrengthText() {
      if (this.passwordStrength <= 25) return 'Faible'
      if (this.passwordStrength <= 50) return 'Moyen'
      if (this.passwordStrength <= 75) return 'Bon'
      return 'Fort'
    },

    canSubmit() {
      return (
        this.passwordForm.password
        && this.passwordForm.confirm_password
        && this.passwordForm.password === this.passwordForm.confirm_password
        && this.passwordForm.password.length >= 8
      )
    },
  },

  created() {
    this.user = storeAuth.state.user
    // Check if user is admin
    if (this.user.role !== 'admin') {
      this.error = "Vous n'avez pas l'autorisation de changer les mots de passe."
    } else {
      this.getSupplierDetails()
    }
  },

  methods: {
    async getSupplierDetails() {
      this.isLoading = true
      try {
        const { id } = this.$route.params
        const { data } = await axios.get(`/api/auth/users/suppliers/${id}/`)
        this.supplier = data
      } catch (error) {
        console.error('Error fetching supplier details:', error)
        this.error = 'Erreur lors du chargement des informations du fournisseur'
      } finally {
        this.isLoading = false
      }
    },

    async onSubmit() {
      if (!this.canSubmit) return

      this.isSubmitting = true
      try {
        const { id } = this.$route.params
        await axios.post(`/api/auth/users/suppliers/change-password/${id}/`, this.passwordForm)

        this.showToast('success', 'Mot de passe modifié avec succès')
        this.$router.push({ name: 'supplier-show', params: { id } })
      } catch (error) {
        console.error('Error changing password:', error)
        this.showToast('danger', error.response?.data?.error || 'Erreur lors du changement de mot de passe')
      } finally {
        this.isSubmitting = false
      }
    },

    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Succès' : 'Erreur',
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
          text,
          variant,
        },
      }, {
        position: 'top-right',
      })
    },
  },
}
</script>
